import React from "react";
import { Loader2, Sparkles } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import PortfolioSummaryCards from "@/components/portfolio/PortfolioSummaryCards";
import PortfolioGrowthChart from "@/components/portfolio/PortfolioGrowthChart";
import HoldingsTable from "@/components/portfolio/HoldingsTable";
import ClosedPositionsTable from "@/components/portfolio/ClosedPositionsTable";
import PLBreakdownCard from "@/components/portfolio/PLBreakdownCard";
import PortfolioInsightCards from "@/components/portfolio/PortfolioInsightCards";
import PortfolioAIReview from "@/components/portfolio/PortfolioAIReview";
import LoadingMessageRotator from "@/components/portfolio/LoadingMessageRotator";

/**
 * Phase 8.WLNAV.v1.5 — Shared presentational Portfolio dashboard.
 *
 * The single UI source-of-truth for the Portfolio dashboard.
 * Used by BOTH:
 *   • Real Premium Portfolio (via `pages/Portfolio.js` container)     → previewMode=false
 *   • Locked / non-Premium Portfolio Preview (via LockedPortfolioPreview) → previewMode=true
 *
 * Renders the exact JSX tree that used to live inline in Portfolio.js
 * happy path. All fetches, mutations and state remain OUTSIDE this
 * component — inputs are the props described below.
 *
 * Props:
 *   data                — full portfolio payload (summary, positions, snapshots,
 *                          review, refresh, pl_breakdown, review_meta,
 *                          admin_qa_bypass). Same shape as `GET /api/portfolio`.
 *   closedPositions     — array from `GET /api/portfolio/closed-positions`
 *                          (or a static demo list in preview mode).
 *   dominantCurrency    — resolved by container.
 *   previewMode         — when true:
 *                          • Refresh meta strip hidden
 *                          • Completeness warning hidden
 *                          • AI generation banner hidden
 *                          • CTA overlay rendered above the chart (desktop)
 *                          • Subcomponents receive previewMode=true (see spec)
 *                          • Closed Positions rendered as static collapsed header
 *   refreshAnalysisBusy — from container (governs the AI generation banner).
 *   ctaOverlay          — React node rendered as absolute overlay above the
 *                          chart card + as a mobile-flow node below the chart.
 *                          Only used when previewMode=true.
 *   chartFetchOverride  — optional async (rangeKey) => performancePayload.
 *                          Preview mode passes a synchronous demo returner
 *                          to prevent any `/api/portfolio/performance` call.
 *   onAddPurchase, onRecordSale, onViewTransactions, onEditMetadata,
 *   onDeleteIncorrect, onViewClosedHistory
 *                       — Premium action handlers. Ignored in previewMode.
 */
export default function PortfolioDashboardView({
  data,
  closedPositions = [],
  dominantCurrency = "USD",
  previewMode = false,
  refreshAnalysisBusy = false,
  ctaOverlay = null,
  chartFetchOverride = null,
  onAddPurchase,
  onRecordSale,
  onViewTransactions,
  onEditMetadata,
  onDeleteIncorrect,
  onViewClosedHistory,
}) {
  const { t } = useTranslation();

  const summary = data?.summary || {};
  const positions = React.useMemo(() => data?.positions || [], [data?.positions]);
  const snapshots = data?.snapshots || [];
  const review = data?.review || null;
  const reviewMeta = data?.review_meta || null;
  const refreshState = data?.refresh || {};
  const refreshesLeft = refreshState?.analysis?.refreshes_left_today ?? null;
  const lastAnalysisAt = refreshState?.analysis?.last_refresh_at;

  // Phase 8.B QA-9 helper (verbatim from container previous location).
  const analysisNextAt = refreshState?.analysis?.next_available_at;
  const analysisDailyResetAt = refreshState?.analysis?.daily_reset_at;
  const analysisAvailabilityText = React.useMemo(() => {
    if (previewMode) return null;
    if (refreshesLeft !== null && refreshesLeft <= 0) {
      if (analysisDailyResetAt) {
        try {
          const reset = new Date(analysisDailyResetAt);
          const localeTag = (typeof navigator !== "undefined" && navigator.language)
            ? navigator.language
            : "en";
          const opts = (t("portfolio.refresh.available_tomorrow") || "").startsWith("Morgen")
            ? { hour: "2-digit", minute: "2-digit", hour12: false }
            : { hour: "2-digit", minute: "2-digit" };
          const hhmm = reset.toLocaleTimeString(localeTag, opts);
          return t("portfolio.refresh.available_tomorrow_at").replace("{time}", hhmm);
        } catch {
          /* fall through */
        }
      }
      return t("portfolio.refresh.available_tomorrow");
    }
    if (!analysisNextAt) return null;
    try {
      const diffMs = new Date(analysisNextAt).getTime() - Date.now();
      if (diffMs <= 0) return null;
      const minutes = Math.ceil(diffMs / 60000);
      return t("portfolio.refresh.next_in_minutes").replace("{n}", String(minutes));
    } catch {
      return null;
    }
  }, [previewMode, t, refreshesLeft, analysisDailyResetAt, analysisNextAt]);

  // --- Refresh meta strip (Premium only; hidden in preview) ---
  const renderRefreshMeta = !previewMode && positions.length > 0 ? (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="portfolio-refresh-meta">
      <span data-testid="portfolio-refreshes-left">
        {t("portfolio.refresh.refreshes_left")}: <span className="text-foreground tabular">{refreshesLeft ?? "—"}/{refreshState?.analysis?.daily_limit ?? 3}</span>
      </span>
      {lastAnalysisAt ? (
        <span>
          {t("portfolio.refresh.last_analysis")}: {new Date(lastAnalysisAt).toLocaleString()}
        </span>
      ) : null}
      {analysisAvailabilityText ? (
        <span data-testid="portfolio-refresh-availability">{analysisAvailabilityText}</span>
      ) : null}
    </div>
  ) : null;

  // --- Completeness warning (Premium only; hidden in preview) ---
  //
  // 2026-02 Staging regression follow-up / GitHub-review iteration 2.
  //
  // Classify the UNCOVERED holdings PER-POSITION so a mixed portfolio
  // (e.g. 2 analysed + 4 never-analysed) reads honestly as "4 holdings
  // awaiting analysis" rather than "4 holdings have Fair Value
  // unavailable".  Three evidence-based buckets:
  //
  //   (a) refresh_in_progress — the AI refresh is currently running;
  //       takes precedence over per-position state because it is a
  //       transient site-wide state.
  //   (b) analysis_missing   — holding has no analysis_ts AND
  //       analysis_available !== true → never analysed yet.
  //   (c) data_unavailable   — holding was analysed but FV is still
  //       null → the deterministic engine declined (e.g. unreconciled
  //       ADR, limited analysis).  We never label this "plan
  //       restricted" without explicit evidence; the copy stays
  //       truthful about what we DO know.
  //
  // When both (b) and (c) exist we render BOTH counts, so a user with
  // 2 pending + 2 unavailable sees that nuance instead of a flat label.
  const uncoveredPositions = React.useMemo(() => {
    return positions.filter((p) => {
      if (!p) return false;
      if (p.fair_value != null && Number.isFinite(Number(p.fair_value))) return false;
      // Exclude explicit "no shares" or deleted entries if the API ever
      // slips any through (positions already filters deleted_at upstream).
      if ((Number(p.shares) || 0) <= 0) return false;
      return true;
    });
  }, [positions]);

  const uncoveredBuckets = React.useMemo(() => {
    let neverAnalysed = 0;
    let analysedUnavailable = 0;
    for (const p of uncoveredPositions) {
      const wasAnalysed = p?.analysis_available === true || !!p?.analysis_ts;
      if (wasAnalysed) analysedUnavailable += 1;
      else neverAnalysed += 1;
    }
    return { neverAnalysed, analysedUnavailable };
  }, [uncoveredPositions]);

  const renderCompletenessWarning = React.useMemo(() => {
    if (previewMode || positions.length === 0) return null;
    const dc = summary?.data_completeness;
    const excluded = summary?.excluded_holdings_count || 0;
    if (dc === null || dc === undefined) return null;
    if (dc >= 0.999 || excluded === 0) return null;
    const pctStr = `${Math.round(dc * 100)}`;
    const { neverAnalysed, analysedUnavailable } = uncoveredBuckets;

    // Build 1-N clauses so each population gets its own honest count.
    //
    // 2026-02 GitHub-review iteration 3: per-clause copy must NOT
    // repeat the overall coverage percentage.  The coverage summary
    // appears ONCE at the top of the warning; each clause then
    // carries only its OWN count + a short explanation.  Preserves
    // the small-card polish on mobile.
    const clauses = [];
    const singular = t("portfolio.completeness.holdings_singular") || "1 holding";
    const plural = t("portfolio.completeness.holdings_plural") || "{count} holdings";
    const fmt = (n) => (n === 1 ? singular
      : plural.replace("{count}", String(n)));

    if (refreshAnalysisBusy) {
      clauses.push({
        state: "refresh_in_progress",
        headline: t("portfolio.completeness.state_refresh_in_progress"),
        holdingsLabel: null,
      });
    } else {
      if (neverAnalysed > 0) {
        clauses.push({
          state: "analysis_missing",
          headline: t("portfolio.completeness.state_analysis_missing"),
          count: neverAnalysed,
          holdingsLabel: fmt(neverAnalysed),
        });
      }
      if (analysedUnavailable > 0) {
        clauses.push({
          state: "data_unavailable",
          headline: t("portfolio.completeness.state_data_unavailable"),
          count: analysedUnavailable,
          holdingsLabel: fmt(analysedUnavailable),
        });
      }
    }
    if (clauses.length === 0) return null;

    // Attribute carries the SET of states so e2e tests can assert
    // mixed scenarios without parsing locale copy.
    const statesAttr = clauses.map((c) => c.state).join(",");

    // Short per-clause detail — count + short explanation ONLY.
    // The coverage percentage lives once at the top of the warning
    // (coverage_summary) so a 3-bucket mixed scenario no longer
    // reads the same percentage three times.
    const clauseDetailTemplate = (
      t("portfolio.completeness.clause_detail") ||
      "{holdings} affected"
    );

    return (
      <div
        data-testid="portfolio-completeness-warning"
        data-completeness-state={statesAttr}
        data-never-analysed-count={neverAnalysed}
        data-analysed-unavailable-count={analysedUnavailable}
        className="rounded-lg border border-[#FFB72B]/40 bg-[rgba(255,183,43,0.06)] px-3 py-2 text-xs space-y-1"
      >
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-[#FFB72B] shrink-0" />
          <span
            data-testid="portfolio-completeness-coverage"
            className="text-foreground/70"
          >
            {t("portfolio.completeness.coverage_summary", {
              pct: pctStr,
            }) || `Fair Value coverage: ${pctStr}% of portfolio value`}
          </span>
        </div>
        {clauses.map((c) => (
          <div
            key={c.state}
            data-testid={`portfolio-completeness-clause-${c.state}`}
            className="flex items-center gap-2 flex-wrap pl-3.5"
          >
            <span
              data-testid={`portfolio-completeness-headline-${c.state}`}
              className="text-[#FFB72B] font-medium"
            >
              {c.headline}
            </span>
            {c.holdingsLabel ? (
              <span
                data-testid={`portfolio-completeness-detail-${c.state}`}
                className="text-foreground/70"
              >
                {clauseDetailTemplate.replace("{holdings}", c.holdingsLabel)}
              </span>
            ) : null}
          </div>
        ))}
      </div>
    );
  }, [
    previewMode, positions.length, summary?.data_completeness,
    summary?.excluded_holdings_count, refreshAnalysisBusy,
    uncoveredBuckets, t,
  ]);

  // --- AI generation banner (Premium only; hidden in preview) ---
  const renderAIGenerationBanner = !previewMode && positions.length > 0 && refreshAnalysisBusy ? (
    <div
      data-testid="portfolio-ai-generation-banner"
      className="relative overflow-hidden rounded-xl border border-[#3DEBFF]/40 bg-[rgba(61,235,255,0.06)] px-4 sm:px-5 py-3.5 shadow-[0_0_24px_rgba(61,235,255,0.18)]"
    >
      <div className="absolute top-0 left-4 right-4 h-[2px] rounded-full bg-[#3DEBFF] shadow-[0_0_12px_rgba(61,235,255,0.6)] animate-pulse" />
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(61,235,255,0.12)] border border-[#3DEBFF]/40">
          <Sparkles className="h-4 w-4 text-[#3DEBFF] animate-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#3DEBFF] font-medium">
              {t("portfolio.review.badge_premium_ai")}
            </span>
            <Loader2 className="h-3 w-3 text-[#3DEBFF]/80 animate-spin" />
          </div>
          <div className="mt-0.5 text-[16px] sm:text-[17px] leading-snug font-medium text-foreground">
            <LoadingMessageRotator />
          </div>
        </div>
      </div>
    </div>
  ) : null;

  // --- Chart + optional CTA overlay ---
  const renderChart = (
    <div className="relative" data-testid="portfolio-dashboard-chart-wrapper">
      <PortfolioGrowthChart
        snapshots={snapshots}
        summary={summary}
        fetchOverride={chartFetchOverride}
        previewMode={previewMode}
      />
      {previewMode && ctaOverlay ? (
        <div
          className="hidden md:flex absolute inset-0 items-center justify-center pointer-events-none z-10"
          data-testid="portfolio-preview-cta-overlay-desktop"
        >
          <div className="pointer-events-auto">{ctaOverlay}</div>
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      <PortfolioSummaryCards summary={summary} dominantCurrency={dominantCurrency} previewMode={previewMode} />
      {renderRefreshMeta}
      {renderCompletenessWarning}
      {renderAIGenerationBanner}
      {renderChart}
      {previewMode && ctaOverlay ? (
        <div className="md:hidden flex justify-center" data-testid="portfolio-preview-cta-mobile">
          {ctaOverlay}
        </div>
      ) : null}
      <PortfolioInsightCards insights={summary?.insights} />
      <HoldingsTable
        positions={positions}
        onAddPurchase={onAddPurchase}
        onRecordSale={onRecordSale}
        onViewTransactions={onViewTransactions}
        onEditMetadata={onEditMetadata}
        onDeleteIncorrect={onDeleteIncorrect}
        previewMode={previewMode}
      />
      <PLBreakdownCard breakdown={data?.pl_breakdown} dominantCurrency={dominantCurrency} previewMode={previewMode} />
      <ClosedPositionsTable
        closedPositions={closedPositions}
        onViewHistory={onViewClosedHistory}
        previewMode={previewMode}
      />
      {review ? (
        <PortfolioAIReview
          review={review.review}
          generatedAt={review.generated_at}
          loading={!previewMode && refreshAnalysisBusy}
          cached={true}
          reviewMeta={reviewMeta}
          previewMode={previewMode}
        />
      ) : !previewMode && refreshAnalysisBusy ? (
        <PortfolioAIReview review={null} loading={true} />
      ) : null}
      <div className="text-[11px] text-muted-foreground text-center pt-4">
        {t("portfolio.page.disclaimer")}
      </div>
    </>
  );
}
